// Zustand store for simulation state management

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

interface SimulationState {
  // Simulation state
  isPlaying: boolean
  particleCount: number
  gravityStrength: number
  particleSize: number
  particleColor: string
  simulationSpeed: number
  
  // Performance
  qualityLevel: 'low' | 'medium' | 'high' | 'ultra'
  
  // Actions
  togglePlayPause: () => void
  resetSimulation: () => void
  updateSettings: (settings: Partial<SimulationState>) => void
  adaptPerformance: (direction: 'increase' | 'decrease') => void
  exportConfiguration: () => void
}

export const useSimulationStore = create<SimulationState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        isPlaying: true,
        particleCount: 10000,
        gravityStrength: 1.0,
        particleSize: 0.5,
        particleColor: '#00b4d8',
        simulationSpeed: 1.0,
        qualityLevel: 'high',
        
        // Actions
        togglePlayPause: () => set((state) => ({ isPlaying: !state.isPlaying })),
        
        resetSimulation: () => set({
          particleCount: 10000,
          gravityStrength: 1.0,
          particleSize: 0.5,
          particleColor: '#00b4d8',
          simulationSpeed: 1.0,
        }),
        
        updateSettings: (settings) => set(settings),
        
        adaptPerformance: (direction) => set((state) => {
          const levels: Array<SimulationState['qualityLevel']> = ['low', 'medium', 'high', 'ultra']
          const currentIndex = levels.indexOf(state.qualityLevel)
          const newIndex = direction === 'increase' 
            ? Math.min(currentIndex + 1, levels.length - 1)
            : Math.max(currentIndex - 1, 0)
          
          const qualityPresets = {
            low: { particleCount: 5000, particleSize: 0.8 },
            medium: { particleCount: 10000, particleSize: 0.6 },
            high: { particleCount: 20000, particleSize: 0.5 },
            ultra: { particleCount: 50000, particleSize: 0.3 }
          }
          
          const newLevel = levels[newIndex]
          return {
            qualityLevel: newLevel,
            ...qualityPresets[newLevel]
          }
        }),
        
        exportConfiguration: () => {
          const state = get()
          const config = {
            particleCount: state.particleCount,
            gravityStrength: state.gravityStrength,
            particleSize: state.particleSize,
            particleColor: state.particleColor,
            simulationSpeed: state.simulationSpeed,
            qualityLevel: state.qualityLevel
          }
          
          const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `galaxy-simulation-${Date.now()}.json`
          a.click()
          URL.revokeObjectURL(url)
        }
      }),
      {
        name: 'galaxy-simulation-storage',
        partialize: (state) => ({
          qualityLevel: state.qualityLevel,
          particleColor: state.particleColor
        })
      }
    )
  )
)